import { useEffect, useRef, useState } from "react";
import { Table, Pagination, Placeholder, Card, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import "../styles/DataTable.css";

export default function DataTable(props) {
	const {
		columns,
		eid,
		data,
		count,
		tableControls: { page, length, sortField, sortDir, search },
		setTableControls,
		deregister,
		downloadButton,
		selectSA,
		showSearch = true,
		searchPlaceholder = "Search anything...",
		onRowClick,
	} = props;

	const [searchInput, setSearchInput] = useState(search || "");
	const skipNextSyncRef = useRef(false);

	useEffect(() => {
		if (skipNextSyncRef.current) {
			skipNextSyncRef.current = false;
			return;
		}
		setSearchInput(search || "");
	}, [search]);

	useEffect(() => {
		if ((search || "") === searchInput) return;
		const handle = setTimeout(() => {
			skipNextSyncRef.current = true;
			setTableControls({
				...props.tableControls,
				page: 1,
				search: searchInput,
			});
		}, 400);
		return () => clearTimeout(handle);
	}, [searchInput]);

	const controls = (
		<div className="data-table-controls">
			<Button variant="primary" style={{ height: "fit-content" }}>
				Total : {count}
			</Button>

			{showSearch && (
				<div className="data-control-section" style={{ flex: "1 1 240px", minWidth: "200px" }}>
					<Form.Control
						type="text"
						placeholder={searchPlaceholder}
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						style={{ maxWidth: "360px" }}
					/>
				</div>
			)}

			<div className="data-control-section">
				<span>Page :</span>
				<Pagination className="mb-0">
					{page > 2 && (
						<Pagination.Item
							onClick={() =>
								setTableControls({
									...props.tableControls,
									page: 1,
								})
							}
						>
							{1}
						</Pagination.Item>
					)}
					{page > 3 && <Pagination.Ellipsis />}
					{page > 1 && (
						<Pagination.Item
							onClick={() =>
								setTableControls({
									...props.tableControls,
									page: page - 1,
								})
							}
						>
							{page - 1}
						</Pagination.Item>
					)}
					<Pagination.Item active>{page}</Pagination.Item>
					{page < Math.ceil(count / length) && (
						<Pagination.Item
							onClick={() =>
								setTableControls({
									...props.tableControls,
									page: page + 1,
								})
							}
						>
							{page + 1}
						</Pagination.Item>
					)}
					{page < Math.ceil(count / length) - 2 && <Pagination.Ellipsis />}
					{page < Math.ceil(count / length) - 1 && (
						<Pagination.Item
							onClick={() =>
								setTableControls({
									...props.tableControls,
									page: Math.ceil(count / length),
								})
							}
						>
							{Math.ceil(count / length)}
						</Pagination.Item>
					)}
				</Pagination>
			</div>

			{/* Rows per page */}
			<div className="data-control-section">
				<span>Show rows per page:</span>
				<Pagination className="mb-0">
					{[10, 20, 50, 100].map((val) => (
						<Pagination.Item
							key={val}
							active={length === val}
							onClick={() =>
								setTableControls({
									...props.tableControls,
									length: val,
									page: 1,
								})
							}
						>
							{val}
						</Pagination.Item>
					))}
				</Pagination>
			</div>

			{/* Download button (if exists) */}
			<div style={{ height: "fit-content" }}>{downloadButton}</div>

		</div >
	);

	return (
		<>
			{controls}
			<Table striped hover responsive>
				<thead>
					<tr>
						{Object.keys(columns).map((val, index) => (
							<th
								key={index}
								onClick={() => {
									if (val === "roll" || val === "eventID" || val === "paymentStatus") return;
									if (sortField === val)
										setTableControls({
											...props.tableControls,
											sortDir: -sortDir,
										});
									else {
										setTableControls({
											...props.tableControls,
											page: 1,
											sortField: val,
											sortDir: 1,
										});
									}
								}}
								style={{ cursor: val === "roll" || val === "eventID" || val === "paymentStatus" ? "default" : "pointer" }}
							>
								{columns[val] + " "}
								{sortField === val &&
									(sortDir > 0 ? (
										<i className="fa fa-caret-up" aria-hidden="true"></i>
									) : (
										<i className="fa fa-caret-down" aria-hidden="true"></i>
									))}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{data.map((part, idx) => (
						<tr
							key={idx}
							onClick={onRowClick ? () => onRowClick(part) : undefined}
							style={onRowClick ? { cursor: "pointer" } : undefined}
						>
							{Object.keys(columns).map((val, index) => (
								<td
									key={index}
									onClick={
										val === "pid" || val === "saID" || val === "pids" || val === "roll" || val === "eventID" || val === "selected"
											? (e) => e.stopPropagation()
											: undefined
									}
								>
									{val in part ? (
										val === "_id" ? (
											sortDir === -1
												? count - (length * (page - 1) + idx)
												: length * (page - 1) + idx + 1
										) : val === "pid" ? (
											<Link
												to={"/participant/" + part[val]}
												style={{ textDecoration: "none" }}
											>
												{part[val]}
											</Link>
										) : val === "saID" ? (
											<Link
												to={"/sa/" + part[val]}
												style={{ textDecoration: "none" }}
											>
												{part[val]}
											</Link>
										) : val === "pids" ? (
											part[val].map((pid, i) => (
												<Link
													key={i}
													to={"/participant/" + pid}
													style={{
														display: "block",
														textDecoration: "none",
													}}
												>
													{pid}
												</Link>
											))
										) : val === "subLink" && part[val] ? (
											<a href={part[val]} target="_blank" rel="noreferrer">
												Link
											</a>
										) : val === "events" ? (
											part.events[eid].submission ? (
												eid === 2 ? (
													part.events[eid].submission
												) : (
													<a
														href={part.events[eid].submission}
														target="_blank"
														rel="noreferrer"
													>
														Link
													</a>
												)
											) : (
												""
											)
										) : val === "roll" || val === "eventID" ? (
											<Button
												variant="danger"
												onClick={() => {
													if (val === "roll") deregister(part.pid);
													else deregister(part.pids[0]);
												}}
											>
												De-Reg
											</Button>
										) : val === "email" ? (
											<a href={"mailto:" + part[val]}>{part[val]}</a>
										) : val === "selected" ? (
											part[val] ? (
												"Yes"
											) : (
												<Button
													variant="primary"
													onClick={() => selectSA(part["_id"])}
												>
													Select
												</Button>
											)
										) : val === "payment" ? (
											part[val].time
										) : val === "cid" ? (
											<a
												href={"https://nssc.in/certificate/" + part[val]}
												target="_blank"
												rel="noreferrer"
											>
												Show Certificate
											</a>
										) : (
											part[val]
										)
									) : (
										<Placeholder as={Card.Text} animation="glow">
											<Placeholder xs={6} />
										</Placeholder>
									)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</Table>
			{controls}
		</>
	);
}
